/**
 * useMerchantCouponDetails Hook - Aggregator Pattern Implementation
 *
 * This hook aggregates individual merchant coupon details:
 * - Fetches coupon information by ID for a merchant
 * - Provides computed properties for display
 * - Calculates expiry status, discount preview, and analytics
 *
 * Pattern: AGGREGATOR
 * - Fetches and aggregates single merchant coupon data
 * - Provides computed display values
 * - Single loading state for UI
 * - Read-only data aggregation
 *
 * IMPORTANT: This hook provides coupon data only.
 * For coupon actions (update, delete), use useMerchantCouponActions coordinator hook.
 */

import { useGetCoupon } from '@/generated/hooks';
import {
	formatForDisplay,
	formatMoney,
	toPercentage,
	TimestampService,
} from '@zlinkx/shared-v2';
import type { ApiTimestamp, GetCouponResponseType } from '@zlinkx/shared-v2';
import type { AggregatorPattern } from '../../types/hook-patterns.types';

// ============================================================================
// TYPES
// ============================================================================

type CouponDetailData = GetCouponResponseType['data'];
type CouponAnalytics = CouponDetailData['analytics'];
type CouponDateInput = CouponDetailData['expires_at'] | ApiTimestamp | Date | number | null | undefined;

/**
 * Options for useMerchantCouponDetails hook
 */
interface UseMerchantCouponDetailsOptions {
	/**
	 * Merchant ID
	 */
	merchantId: number;
	/**
	 * Coupon ID
	 */
	couponId: number;
	/**
	 * Enable/disable fetching
	 */
	enabled?: boolean;
}

/**
 * Raw coupon data extracted from API response
 */
interface MerchantCouponDetailsData {
	coupon: CouponDetailData | null;
	couponId: CouponDetailData['coupon_id'] | null;
	title: string;
	description: string | null;
	couponType: string;
	couponValue: number;
	minimumAmount: number | null;
	commissionBasisPoints: number;
	expiresAt: CouponDetailData['expires_at'];
	isActive: boolean;
	backgroundImageUrl: CouponDetailData['background_image_url'];
	titleFr: CouponDetailData['title_fr'];
	titleZh: CouponDetailData['title_zh'];
	distributedAt: CouponDetailData['distributed_at'];
	analytics: CouponAnalytics | null;
}

/**
 * Computed coupon values for UI display
 */
interface MerchantCouponDetailsComputed {
	// Status flags
	isExpired: boolean;
	isActive: boolean;
	isLocked: boolean;
	hasMinimumAmount: boolean;
	hasMultipleLanguages: boolean;

	// Time calculations
	daysUntilExpiry: number | null;

	// Formatted displays
	formattedExpiryDate: string | null;
	discountPreview: string;
	commissionDisplay: string;
	minimumAmountDisplay: string | null;
	languagesList: string[];

	// Analytics computed
	redemptionRate: string;
	viewRate: string;
	hasSharers: boolean;
}

/**
 * Return type for useMerchantCouponDetails hook
 */
type UseMerchantCouponDetailsReturn = AggregatorPattern<
	MerchantCouponDetailsData,
	MerchantCouponDetailsComputed
>;

// ============================================================================
// HELPER FUNCTIONS (Pure)
// ============================================================================

/**
 * Calculate days until a date
 */
function parseCouponDate(dateValue: CouponDateInput): Date | null {
	if (dateValue === null || dateValue === undefined) return null;

	if (dateValue instanceof Date) {
		return TimestampService.isValidDate(dateValue) ? dateValue : null;
	}

	if (typeof dateValue === 'number') {
		const ms = dateValue < 10000000000 ? dateValue * 1000 : dateValue;
		const parsed = TimestampService.getCurrentDate();
		parsed.setTime(ms);
		return TimestampService.isValidDate(parsed) ? parsed : null;
	}

	const parsed = TimestampService.parseDateTimeInput(dateValue);
	return TimestampService.isValidDate(parsed) ? parsed : null;
}

function calculateDaysUntil(dateValue: CouponDateInput): number | null {
	const targetDate = parseCouponDate(dateValue);
	if (!targetDate) return null;

	const diffMs = -TimestampService.getMillisecondsSince(targetDate);
	const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

	return diffDays;
}

/**
 * Format date for display
 */
function formatDate(dateValue: CouponDateInput): string | null {
	const date = parseCouponDate(dateValue);
	if (!date) return null;

	return formatForDisplay(date, { style: 'long' });
}

/**
 * Format discount preview
 */
function formatDiscountPreview(couponType: string | undefined, couponValue: number | undefined): string {
	if (!couponType || couponValue === undefined) return '';

	switch (couponType) {
		case 'percentage':
			return `${couponValue}% OFF`;
		case 'fixed_amount':
			return `${formatMoney(couponValue)} OFF`;
		case 'free_item':
			return 'Free Item';
		case 'bogo':
			return 'Buy One Get One';
		default:
			return '';
	}
}

/**
 * Calculate rate with division by zero protection
 */
function calculateRate(numerator: number, denominator: number): string {
	if (denominator === 0) return 'N/A';
	return `${((numerator / denominator) * 100).toFixed(1)}%`;
}

/**
 * Get list of available languages
 */
function getLanguagesList(coupon: CouponDetailData | null): string[] {
	if (!coupon) return [];
	const languages = ['English'];
	if (coupon.title_fr) languages.push('French');
	if (coupon.title_zh) languages.push('Chinese');
	return languages;
}

function hasCouponDataWrapper(value: unknown): value is GetCouponResponseType {
	return typeof value === 'object' && value !== null && 'data' in value;
}

function extractCouponData(value: unknown): CouponDetailData | undefined {
	if (value === null || value === undefined) return undefined;
	if (hasCouponDataWrapper(value)) return value.data;
	return value as CouponDetailData;
}

// ============================================================================
// HOOK IMPLEMENTATION
// ============================================================================

/**
 * Aggregates individual merchant coupon details with computed properties
 *
 * @example
 * ```tsx
 * function CouponDetailPage({ merchantId, couponId }: Props) {
 *   const couponDetails = useMerchantCouponDetails({
 *     merchantId,
 *     couponId,
 *   });
 *
 *   if (couponDetails.isLoading) {
 *     return <LoadingSpinner />;
 *   }
 *
 *   if (couponDetails.error) {
 *     return <ErrorMessage message={couponDetails.error} />;
 *   }
 *
 *   const { data, computed } = couponDetails;
 *
 *   return (
 *     <div>
 *       <h1>{data.title}</h1>
 *       <p>{computed.discountPreview}</p>
 *       <p>Status: {computed.isActive ? 'Active' : 'Expired'}</p>
 *       {computed.isLocked && <LockedBadge />}
 *     </div>
 *   );
 * }
 * ```
 */
export function useMerchantCouponDetails(
	options: UseMerchantCouponDetailsOptions,
): UseMerchantCouponDetailsReturn {
	const { merchantId, couponId, enabled = true } = options;

	// ========================================
	// Data Fetching
	// ========================================

	const couponQuery = useGetCoupon(
		{ merchantId, couponId },
		{
			enabled: enabled && !!merchantId && !!couponId,
		},
	);

	// ========================================
	// Data Processing
	// ========================================

	const coupon = extractCouponData(couponQuery.data);

	const data: MerchantCouponDetailsData = {
		coupon: coupon || null,
		couponId: coupon?.coupon_id || null,
		title: coupon?.title || '',
		description: coupon?.description || null,
		couponType: coupon?.coupon_type || '',
		couponValue: coupon?.coupon_value ?? 0,
		minimumAmount: coupon?.minimum_amount ?? null,
		commissionBasisPoints: coupon?.commission_basis_points ?? 0,
		expiresAt: coupon?.expires_at || null,
		isActive: coupon?.is_active ?? false,
		backgroundImageUrl: coupon?.background_image_url || null,
		titleFr: coupon?.title_fr || null,
		titleZh: coupon?.title_zh || null,
		distributedAt: coupon?.distributed_at ?? null,
		analytics: coupon?.analytics || null,
	};

	// ========================================
	// Computed Values
	// ========================================

	const daysUntilExpiry = calculateDaysUntil(data.expiresAt);
	const isExpired = daysUntilExpiry !== null && daysUntilExpiry <= 0;
	const languages = getLanguagesList(coupon || null);

	const computed: MerchantCouponDetailsComputed = {
		// Status flags
		isExpired,
		isActive: data.isActive && !isExpired,
		isLocked: !!data.distributedAt,
		hasMinimumAmount: data.minimumAmount !== null && data.minimumAmount > 0,
		hasMultipleLanguages: languages.length > 1,

		// Time calculations
		daysUntilExpiry,

		// Formatted displays
		formattedExpiryDate: formatDate(data.expiresAt),
		discountPreview: formatDiscountPreview(data.couponType, data.couponValue),
		commissionDisplay: `${toPercentage(data.commissionBasisPoints)}%`,
		minimumAmountDisplay: data.minimumAmount ? formatMoney(data.minimumAmount) : null,
		languagesList: languages,

		// Analytics computed
		redemptionRate: data.analytics
			? calculateRate(data.analytics.total_redeemed, data.analytics.total_distributed)
			: 'N/A',
		viewRate: data.analytics
			? calculateRate(data.analytics.total_views, data.analytics.total_distributed)
			: 'N/A',
		hasSharers: (data.analytics?.top_sharers?.length ?? 0) > 0,
	};

	// ========================================
	// Return Aggregator Pattern
	// ========================================

	return {
		data,
		computed,
		isLoading: couponQuery.isLoading,
		error: couponQuery.error ? String(couponQuery.error.message || 'Failed to load coupon') : null,
	};
}

// ============================================================================
// EXPORTS
// ============================================================================

export default useMerchantCouponDetails;

export type {
	UseMerchantCouponDetailsOptions,
	MerchantCouponDetailsData,
	MerchantCouponDetailsComputed,
	UseMerchantCouponDetailsReturn,
};
